import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import type { TFunction } from '@mukil/i18n';
import {
  Alert,
  Button,
  EmptyState,
  GlassCard,
  StatusPill,
  StyledSelect,
  toast,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { NumericFieldRow } from '../../components/common/field_row';
import { useMyPay } from '../../data/use_my_pay';
import { useSelfLink, type SelfLinkFailure } from '../../data/use_self_link';
import { useSession } from '../../data/use_session';
import { useIsEssSurface } from '../../components/layout/ess_surface';
import { MyPayStepUpPanel } from './my_pay_step_up_panel';
import { MyPaySelfLinkDialog } from './my_pay_self_link_dialog';
import { resolveSelectedPayslip } from './my_pay_selection';
import {
  download_my_payslip,
  PAYSLIP_SAVED_TOAST_DURATION_MS,
  type MyPayDownloadFailure,
} from './my_pay_download';
import {
  format_date,
  format_money,
  format_money_deduction,
  format_month_year,
  type FormatMoneyOptions,
} from '../../lib/format';
import type { PayslipLine } from '../../data/types';
import type { RealEmployerLink } from '../../data/wagepro_store_my_pay';

/**
 * Every amount on a payslip -- net pay, each line, the history rows -- prints
 * with the currency's full decimals, so a whole gross (`LKR 285,000.00`) and a
 * fractional net pay (`LKR 247,300.01`) read in one format (WagePro bug-bash
 * 2026-10-15, #10).
 */
const PAYSLIP_MONEY: FormatMoneyOptions = { fixed_decimals: true };

const LINE_KEY = {
  gross_pay: 'my_pay.line.gross_pay',
  epf_employee: 'my_pay.line.epf_employee',
  apit_withheld: 'my_pay.line.apit_withheld',
  other_deductions: 'my_pay.line.other_deductions',
  other_earnings: 'my_pay.line.other_earnings',
} as const;

/**
 * The translated message for a `link_self` failure. A literal-per-branch
 * function rather than the `Record<SelfLinkFailure, string>` lookup this
 * replaced (`t(SELF_LINK_ERROR_KEY[link_failure])`) — a dynamic
 * object-index argument is invisible to the static i18n key extractor
 * (its regex requires a plain string literal immediately after `t(`), so
 * every one of these four keys was misreported as an orphan despite being
 * genuinely reachable at runtime. See `leave_utils.ts`'s
 * `leave_action_error_message` for the same pattern applied elsewhere in
 * this app.
 */
function self_link_error_message(
  t: TFunction,
  failure: SelfLinkFailure,
): string {
  switch (failure) {
    case 'no_matching_employee':
      return t('my_pay.link_self_error_no_match');
    case 'already_linked_elsewhere':
      return t('my_pay.link_self_error_already_linked');
    // `profile_required` is handled by opening the details dialog, never
    // shown as an error; the rest are dialog-scoped and fall back to generic.
    case 'profile_required':
    case 'plan_cap':
    case 'validation':
    case 'no_entity':
    case 'error':
      return t('my_pay.link_self_error_generic');
  }
}

/**
 * Employee self-service: the current payslip and recent net pay. Wired to
 * the real `personalPayroll.listEmployerLinks`/`listPayslips` procedures
 * (C7 PR B, #21938) via `use_my_pay.ts` — no mock fallback (see that
 * hook's docblock for why).
 */

export function MyPayScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { session, is_employer_session, is_employee_view_only } = useSession();
  // Phone layout on the employee surface only; the employer console's own
  // My pay route keeps the original layout (components/layout/ess_surface.tsx).
  const ess = useIsEssSurface();
  const { state, select_employer, retry } = useMyPay();
  const { link_self } = useSelfLink();
  const [linking, set_linking] = React.useState(false);
  /** `null` = show the latest payslip; otherwise a `history` payslip's id. */
  const [selected_payslip_id, set_selected_payslip_id] = React.useState<
    string | null
  >(null);
  const [link_failure, set_link_failure] =
    React.useState<SelfLinkFailure | null>(null);
  /** The payslip id whose PDF is being generated/saved, if any. */
  const [downloading_id, set_downloading_id] = React.useState<string | null>(
    null,
  );
  /**
   * Keyed by payslip id so switching to another period hides a stale error
   * without an effect to reset it.
   */
  const [download_failure, set_download_failure] = React.useState<{
    payslip_id: string;
    reason: MyPayDownloadFailure;
  } | null>(null);
  // The pay-details dialog for an employer with no employee record yet.
  const [profile_dialog_open, set_profile_dialog_open] = React.useState(false);

  async function handle_link_self(): Promise<void> {
    if (!link_self) return;
    set_link_failure(null);
    set_linking(true);
    const result = await link_self();
    set_linking(false);
    if (result.outcome === 'success') {
      // The employer_link now exists — refetch rather than reload.
      retry();
      return;
    }
    if (result.failure === 'profile_required') {
      // No employee row carries their email yet: collect pay details so the
      // server can create a complete record, rather than showing an error.
      set_profile_dialog_open(true);
      return;
    }
    set_link_failure(result.failure);
  }

  if (state.kind === 'loading') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_pay.title')} />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <span className="sr-only">{t('my_pay.loading')}</span>
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
        </GlassCard>
      </div>
    );
  }

  if (state.kind === 'step_up_required') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_pay.title')} />
        <MyPayStepUpPanel />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_pay.title')} />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState
            title={t('my_pay.error')}
            action={
              <Button variant="secondary" onClick={retry}>
                {t('my_pay.retry')}
              </Button>
            }
          />
        </GlassCard>
      </div>
    );
  }

  if (state.kind === 'no_employer_link') {
    // `is_employer_session` is true only for an EMPLOYER (`use_session.ts`
    // derives it from the boot's session kind, never from the toggled role).
    // Telling that person to "ask your employer to send you an invite" is
    // nonsense — they ARE the employer. Same state, two different readers,
    // so two different explanations.
    //
    // This reads `is_employer_session`, NOT `can_switch_to_admin`: inside the
    // native shell an employer has no admin view to switch to (see
    // `data/employee_view_only.ts`) but is still the employer, and the
    // self-link action below works there exactly as it does in a browser.
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_pay.title')} />
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState
            title={
              is_employer_session
                ? // Three readers, three truths. `no_employer_link_preview`
                  // says "you are previewing the employee view" — accurate
                  // for an employer who toggled into it in a browser and can
                  // toggle back, and wrong inside the native shell, where
                  // there is no other view to return to and no toggle to
                  // point at. The native variant instead says where the
                  // employer half of the product actually lives (the desktop
                  // app), which is also the answer for an employer whose
                  // organisation isn't set up yet: `resolveConnectLanding`
                  // deliberately skips the first-run setup screen on a phone.
                  is_employee_view_only
                  ? t('my_pay.no_employer_link_employer_native')
                  : t('my_pay.no_employer_link_preview')
                : t('my_pay.no_employer_link')
            }
            action={
              // Offered ONLY to an employer: the server matches the caller's
              // own authenticated email against an employee row in a company
              // they administer, so there is nothing here for a real employee
              // to press (they arrive via their invite).
              is_employer_session && link_self ? (
                <Button
                  variant="primary"
                  loading={linking}
                  onClick={() => void handle_link_self()}
                  data-testid="my-pay-link-self"
                >
                  {t('my_pay.link_self_cta')}
                </Button>
              ) : undefined
            }
          />
          {/* Mounted only while open: the dialog reads the organisation
              settings, which a closed dialog has no need to subscribe to. */}
          {link_self && profile_dialog_open && (
            <MyPaySelfLinkDialog
              open
              onClose={() => {
                set_profile_dialog_open(false);
              }}
              submit={link_self}
              onLinked={() => {
                set_profile_dialog_open(false);
                retry();
              }}
            />
          )}
          {link_failure !== null && (
            <p
              className="nv-help is-error mt-3"
              role="alert"
              data-testid="my-pay-link-self-error"
            >
              {self_link_error_message(t, link_failure)}
            </p>
          )}
        </GlassCard>
      </div>
    );
  }

  // `no_payslips` and `ready` both carry `employers`/`selected_entity_id` --
  // the picker below is shared between them, rendered only for a
  // multi-employer caller (§6: shipping single-link-only would silently
  // show one arbitrary employer's data to a multi-employer user).
  const employerPicker =
    state.employers.length > 1 ? (
      <div className="mb-3.5">
        <label className="nv-label" htmlFor="my-pay-employer">
          {t('my_pay.select_employer')}
        </label>
        {/* The wrapper bounds the width: the StyledSelect trigger is `w-full`
            by default, and an employer name does not need the whole column. */}
        <div className="mt-1 w-full sm:w-[280px]">
          <StyledSelect
            id="my-pay-employer"
            value={state.selected_entity_id}
            options={state.employers.map((employer: RealEmployerLink) => ({
              value: employer.entity_id,
              label: employer.entity_name,
            }))}
            onChange={(next) => {
              select_employer(next);
            }}
          />
        </div>
      </div>
    ) : null;

  if (state.kind === 'no_payslips') {
    return (
      <div className="max-w-[720px] mx-auto">
        <ScreenHeader title={t('my_pay.title')} />
        {employerPicker}
        <GlassCard variant="accent" className="rounded-2xl p-5">
          <EmptyState title={t('my_pay.no_payslips')} />
        </GlassCard>
      </div>
    );
  }

  const { my_pay } = state;
  // Which payslip the card shows. `null` means the latest — kept as "null,
  // not an id" so a new pay run landing (or the employer picker switching to
  // a different company, which replaces `my_pay` wholesale) always shows the
  // NEWEST payslip rather than pinning whatever was selected before. A
  // selected id that no longer exists in this employer's list falls back the
  // same way, so a stale selection can never render an empty card.
  const selected = resolveSelectedPayslip(my_pay, selected_payslip_id);
  const viewing_earlier = selected.id !== my_pay.current_payslip.id;
  const payslip = selected;
  const selected_entity_id = state.selected_entity_id;

  async function handle_download(): Promise<void> {
    const payslip_id = payslip.id;
    set_download_failure(null);
    set_downloading_id(payslip_id);
    const result = await download_my_payslip(
      {
        entity_id: selected_entity_id,
        pay_run_id: payslip.pay_run_id,
        locale,
        period: payslip.period,
      },
      {
        title: t('my_pay.downloaded_notification_title'),
        body: t('my_pay.downloaded_notification_body'),
      },
    );
    // Only clear our own spinner: a second download started meanwhile (the
    // user switched period) owns `downloading_id` now.
    set_downloading_id((current) => (current === payslip_id ? null : current));
    if (!result.ok) {
      set_download_failure({ payslip_id, reason: result.reason });
      return;
    }
    if (result.saved_on_device) {
      toast.success(t('my_pay.download_saved'), {
        duration: PAYSLIP_SAVED_TOAST_DURATION_MS,
      });
    }
  }

  const download_failed_reason =
    download_failure?.payslip_id === payslip.id
      ? download_failure.reason
      : null;

  const download_control = (
    <>
      {ess ? (
        // Phone layout: the payslip's one action as the card's primary
        // button -- full width on a phone, natural width from `sm` up. Same
        // handler, label and test id as the console layout below.
        <div className="mt-4 flex sm:justify-end">
          <Button
            variant="primary"
            loading={downloading_id === payslip.id}
            onClick={() => {
              void handle_download();
            }}
            className="w-full sm:w-auto"
            data-testid="my-pay-download-payslip"
          >
            <DownloadIcon />
            {t('my_pay.download_payslip')}
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            loading={downloading_id === payslip.id}
            onClick={() => {
              void handle_download();
            }}
            data-testid="my-pay-download-payslip"
          >
            {t('my_pay.download_payslip')}
          </Button>
        </div>
      )}
      {download_failed_reason !== null && (
        <Alert
          variant="danger"
          className="mt-3"
          title={
            download_failed_reason === 'step_up'
              ? t('my_pay.step_up.description')
              : t('my_pay.download_error')
          }
        />
      )}
    </>
  );

  const back_control = viewing_earlier ? (
    <div className="mb-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          set_selected_payslip_id(null);
        }}
        // `-ms-2` pulls the ghost button's own inline padding back so its
        // LABEL optically aligns with the heading below it rather than
        // sitting indented from it. Logical property, so it stays correct
        // under RTL.
        className="-ms-2"
        data-testid="my-pay-back-to-latest"
      >
        <ChevronStartIcon />
        {t('my_pay.back_to_latest')}
      </Button>
    </div>
  ) : null;

  // Phone layout: net pay leads, then the same lines as label/amount rows
  // under a hairline. Same figures and formatting as the console rows. Rows,
  // not a multi-column grid: five lines squeezed into ~84px columns at a 360px
  // viewport broke "− LKR 14,899.90" across three lines (WagePro bug-bash
  // 2026-10-15, #2). The label takes the leftover width and wraps (si/ta
  // labels run long); the amount never shrinks and never breaks.
  const ess_payslip_body = (
    <>
      <div className="mt-2.5">
        <p className="text-[11px] uppercase tracking-[0.08em] text-[color:var(--ink-muted)]">
          {t('my_pay.net_pay')}
        </p>
        {/* One line at any phone width: the size scales down with the
            viewport (22px at 320px) instead of breaking the figure. */}
        <p
          className="num mt-0.5 whitespace-nowrap text-[clamp(22px,8vw,30px)] font-extrabold leading-[1.1] tracking-[-0.03em] text-[color:var(--accent)]"
          data-testid="my-pay-net-pay-amount"
        >
          {format_money(
            payslip.net_pay,
            payslip.currency,
            locale,
            PAYSLIP_MONEY,
          )}
        </p>
      </div>

      <dl className="mt-3 flex flex-col gap-y-2.5 border-t border-[color:var(--wagepro-ess-divider)] pt-2.5">
        {payslip.lines.map((line) => (
          <div
            key={line.kind}
            className="flex items-baseline justify-between gap-x-3"
          >
            <dt className="min-w-0 break-words text-[11px] text-[color:var(--ink-muted)]">
              {line_label(line, t)}
            </dt>
            <dd
              className="num shrink-0 whitespace-nowrap text-[14px] font-bold"
              data-testid="my-pay-line-amount"
            >
              {line.is_deduction
                ? format_money_deduction(
                    line.amount,
                    payslip.currency,
                    locale,
                    PAYSLIP_MONEY,
                  )
                : format_money(
                    line.amount,
                    payslip.currency,
                    locale,
                    PAYSLIP_MONEY,
                  )}
            </dd>
          </div>
        ))}
      </dl>
    </>
  );

  const payslip_body = ess ? (
    ess_payslip_body
  ) : (
    <>
      {payslip.lines.map((line) => (
        <NumericFieldRow key={line.kind} label={line_label(line, t)}>
          {line.is_deduction
            ? format_money_deduction(
                line.amount,
                payslip.currency,
                locale,
                PAYSLIP_MONEY,
              )
            : format_money(
                line.amount,
                payslip.currency,
                locale,
                PAYSLIP_MONEY,
              )}
        </NumericFieldRow>
      ))}

      <div className="flex justify-between pt-3.5 font-bold text-[16px]">
        <span>{t('my_pay.net_pay')}</span>
        <span className="num">
          {format_money(
            payslip.net_pay,
            payslip.currency,
            locale,
            PAYSLIP_MONEY,
          )}
        </span>
      </div>
    </>
  );

  // Phone layout of the history list: the same rows (period + net pay, the
  // same button, test id and aria-current), laid out edge to edge in one
  // card with a trailing chevron (`.wp-listrow--ess`).
  const ess_history_section = (
    <div className="mt-5">
      <h2 className="mb-2 text-[17px] font-bold tracking-[-0.01em]">
        {t('my_pay.history')}
      </h2>
      <GlassCard className="overflow-hidden rounded-3xl p-0">
        {my_pay.history.length === 0 && (
          <div className="p-5">
            <EmptyState title={t('my_pay.history_empty')} />
          </div>
        )}
        {my_pay.history.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              set_selected_payslip_id(entry.id);
            }}
            className="wp-listrow wp-listrow--ess w-full text-start"
            aria-current={selected.id === entry.id ? 'true' : undefined}
            data-testid="my-pay-history-row"
          >
            <span className="min-w-0 flex-1 text-[15px] font-bold">
              {format_month_year(entry.period, locale)}
            </span>
            <span className="num text-[15px] font-bold">
              {format_money(
                entry.net_pay,
                payslip.currency,
                locale,
                PAYSLIP_MONEY,
              )}
            </span>
            <ChevronEndIcon />
          </button>
        ))}
      </GlassCard>
    </div>
  );

  const history_section = ess ? (
    ess_history_section
  ) : (
    <div className="mt-4">
      <h2 className="font-bold mb-2 text-[14px]">{t('my_pay.history')}</h2>
      <GlassCard className="rounded-2xl p-5">
        {/*
         * `deriveMyPay` puts the most recent payslip in `current_payslip`
         * (the card above) and only the EARLIER ones in `history`, so an
         * employee with exactly one payslip has an empty list here. That
         * rendered as a bare, empty card under a "Pay history" heading —
         * indistinguishable from data failing to load, and it is the state
         * every new employee sees after their first pay run. `EmptyState`
         * says so instead (`docs/design-system/CLAUDE.md`: zero-states are
         * `<EmptyState>`, not empty containers).
         */}
        {my_pay.history.length === 0 && (
          <EmptyState title={t('my_pay.history_empty')} />
        )}
        {my_pay.history.map((entry) => (
          /*
           * A real <button>, not a <div> with an onClick: this is the tap
           * target that opens that period's payslip, so it needs keyboard
           * focus, Enter/Space activation and a button role for assistive
           * tech. `wp-listrow` already lays out as a space-between flex row;
           * `w-full text-start` is all a button needs to look identical to
           * the old div, and `text-start` (not `text-left`) keeps it correct
           * under RTL.
           *
           * Keyed by `id` — the payslip's own primary key, unique even when
           * two pay runs share a period (an off-cycle bonus run, a
           * correction run).
           */
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              set_selected_payslip_id(entry.id);
            }}
            className="wp-listrow text-[13.5px] w-full text-start"
            aria-current={selected.id === entry.id ? 'true' : undefined}
            data-testid="my-pay-history-row"
          >
            <span>{format_month_year(entry.period, locale)}</span>
            <span className="font-semibold num">
              {format_money(
                entry.net_pay,
                payslip.currency,
                locale,
                PAYSLIP_MONEY,
              )}
            </span>
          </button>
        ))}
      </GlassCard>
    </div>
  );

  return (
    <div className="max-w-[720px] mx-auto">
      <ScreenHeader
        title={t('my_pay.title')}
        description={t('my_pay.subtitle', {
          name: session.name,
          employer: my_pay.employer_name,
        })}
      />

      {employerPicker}

      <GlassCard
        variant="accent"
        className={
          ess ? 'wp-ess-hero rounded-[20px] p-4 sm:p-5' : 'rounded-2xl p-5'
        }
      >
        {/* The way back out of a history selection — its own row above the
            heading, where a back affordance is read. It first shipped inside
            the title row next to the StatusPill, where on a phone the three
            items wrapped and left it stranded mid-card. */}
        {back_control}

        <div
          className={
            ess
              ? 'flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5'
              : 'flex items-center justify-between gap-3 mb-3'
          }
        >
          <h2
            className={
              ess
                ? 'text-[13px] font-semibold text-[color:var(--wagepro-ess-ink-soft)]'
                : 'font-bold'
            }
          >
            {t('my_pay.payslip_for', {
              period: format_month_year(payslip.period, locale),
            })}
          </h2>
          <StatusPill
            status="approved"
            label={t('my_pay.issued_on', {
              date: format_date(payslip.issued_on, locale),
            })}
          />
        </div>

        {payslip_body}
        {download_control}
      </GlassCard>

      {history_section}
    </div>
  );
}

/**
 * A "back" chevron. Inline SVG rather than an icon dependency, matching the
 * precedent in `components/layout/employee_top_bar.tsx` and
 * `employee_locale_switcher.tsx`. `rtl:rotate-180` flips it for a
 * right-to-left locale, where "back" points the other way.
 */
function ChevronStartIcon(): React.JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/**
 * The phone history row's "opens" chevron, pointing the reading direction
 * (`rtl:rotate-180`). Decorative -- the row's own text is its name.
 */
function ChevronEndIcon(): React.JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--ink-muted)] rtl:rotate-180"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/** Download glyph for the phone layout's primary action. Decorative. */
function DownloadIcon(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />
    </svg>
  );
}

/**
 * No line kind interpolates a rate. The EPF line specifically does NOT
 * display `PayslipLine`'s `epf_employee.rate` field even though it carries
 * one — see `wagepro_store_my_pay.ts`'s gap #2 doc comment for why showing
 * the pinned statutory rate next to the actual deducted amount would be
 * misleading (EPF is computed on basic salary, which this adapter doesn't
 * separately track).
 */
function line_label(line: PayslipLine, t: (key: string) => string): string {
  return t(LINE_KEY[line.kind]);
}
